import { FC, useMemo, useCallback, memo } from 'react';
import { Mic, MicOff } from 'lucide-react';
import styled from 'styled-components';

import { useAgoraEngine } from '@providers/AgoraEngineProvider';
import { useUpdateStatusMutation } from '@redux/queries/participant';
import { ParticipantStatus } from '@typings/participant';
import SkewedIconButton from '@components/buttons/SkewedIconButton';
import { SKEW_ANGLE } from '@utils/constants';

const MuteIcon = styled(Mic)`
  width: 16px;
  height: 16px;
  color: ${({ theme }) => theme.colors.white};
  transform: skew(${SKEW_ANGLE}deg);
`;

const MicOffIcon = styled(MicOff)`
  width: 16px;
  height: 16px;
  color: ${({ theme }) => theme.colors.red[300]};
  transform: skew(${SKEW_ANGLE}deg);
`;

type Props = {
  participantId?: string | null;
  participantStatus?: ParticipantStatus;
};

const MuteButton: FC<Props> = ({ participantId, participantStatus }) => {
  const { isHost } = useAgoraEngine();
  const isMutedByMyself = useMemo(
    () => participantStatus && participantStatus === ParticipantStatus.Muted,
    [participantStatus],
  );
  const isForceMuted = useMemo(
    () =>
      participantStatus && participantStatus === ParticipantStatus.ForceMuted,
    [participantStatus],
  );
  const isMuted = useMemo(
    () => isMutedByMyself || isForceMuted,
    [isMutedByMyself, isForceMuted],
  );
  const [updateStatus] = useUpdateStatusMutation();

  const onMutePress = useCallback(() => {
    if (isHost && participantId) {
      updateStatus({
        id: participantId,
        status: isMuted
          ? ParticipantStatus.Unmuted
          : ParticipantStatus.ForceMuted,
      });
    }
  }, [isHost, participantId, updateStatus, isMuted]);

  return (
    <SkewedIconButton
      icon={isMuted ? <MicOffIcon /> : <MuteIcon />}
      onClick={onMutePress}
    />
  );
};

export default memo(MuteButton);
